
// Vue
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action, State } from "vuex-class";

import { AudioDevice, AudioTypes, DeviceSpec, AudioX } from "@/types/audio/audio";

import { ResourceType } from "@/constants/resourceTypes";


// Audio Components
import Receive from "./Receive.vue";
import Transmit from "./Transmit.vue";
import TwowayAudio from "./TwowayAudio.vue";
import AudioList from "./AudioList.vue";
import { DeviceType } from '@/store/devices/types';
import { EventDetails, ActiveMapItem } from '@/store/site-monitor/types';
import { MapItemTypeData } from '@/store/map-layers/types';
import { get } from "lodash";

// constants
import MapLayerItemTypes from "@/constants/MapLayerItemTypes";
import { FeaturesList } from '@/store/types';

// vuex modules
const Session = namespace("sessions");
const SiteMonitor = namespace("siteMonitor");
const MapLayers = namespace("mapLayers");

@Component({
    components: {
        "receive": Receive,
        "transmit": Transmit,
        "twoway-audio": TwowayAudio,
        "audio-list": AudioList
    }
})
export default class AudioControl extends Vue {
    // -- vuex -- //

    @Session.Getter private getSession: (sessionId: number | string) => string;
    @Session.Action updateSession: (
        { eventId, resourceId}: { eventId?: number, resourceId: number}) => Promise<void>;
    @SiteMonitor.State activeMapItems: ActiveMapItem[];
    @MapLayers.State(state => state.MapLayersItemTypes) private mapItemTypes: MapItemTypeData[];
    @SiteMonitor.Getter("getEventDetails") eventDetails: EventDetails;
    @Getter("getFeaturesList") private featuresList: FeaturesList;

    // -- Properties -- //

    private audioReceive: AudioDevice = null;
    private audioTransmit: AudioDevice = null;
    private twoWayAudio: AudioDevice = null;
    private auth: string = "";

    // access to enum within the template.
    private audioOptionTypes: typeof AudioTypes = AudioTypes;

    // -- lifecycle hooks -- //

    private async mounted() {
        if (!this.isAudioEnabled) return;
        await this.updateSession({ resourceId: ResourceType.DeviceService });
        this.auth = this.getSession(ResourceType.DeviceService.toString());
    }

    // -- computed properties -- //

    /**
     * @summary computed property which will get the devices nearby *in spotlight circle* and map the object ids
     * @returns { number[] } an array of deviceIds
     */
    private get AudioNearbyDeviceIds(): number[] {
        const result = this.activeMapItems ? this.activeMapItems.filter(x => x.itemType == MapLayerItemTypes.Audio) : [];
        // Map devices to audio
        return result.map(x => x.objectID);
    }

    /**
     * @summary computed property which checks if audio is enabled in the feature list
     * @returns { boolean } if the feature is enabled.
     */
    public get isAudioEnabled(): boolean {
        return get(this.featuresList, ["Integration", "Audio"], false);
    }

    /**
     * @summary method which will return the DeviceServiceEndpoint
     * this method also stricts out the extra bits which could exist like http/s etc.
     */
    private get getDeviceServiceEndpoint(): string {
        return (this.eventDetails && this.eventDetails.devicesEndpoint) ? this.eventDetails.devicesEndpoint.replace(/(^\w+:|^)\/\//, '') : "";
    }

    /**
     * @summary computed property which will return if at least 1 of the audio types are chosen
     */
    private get loadAudioControls() {
       return (this.audioReceive || this.audioTransmit || this.twoWayAudio) ? true : false;
    }

    /**
     * @summary depending on the components rendered, it will change the column size.
     */
    private get displayControlsWidth(): number {
        return (this.audioReceive && this.audioTransmit) ?  6 : 12;
    }

    /**
     * @summary current address for transmitting audio
     */
    private get getTxAddress(): string {
        return this.constructAddress(AudioX.transmit);
    }

    /**
     * @summary current address for retrieving audio
     */
    private get getRxAddress(): string {
        return this.constructAddress(AudioX.receive);
    }

    // -- Methods -- //

    /**
     * @summary method which constructs the websocket url depending on if its secure or unsecure
     * and the type of audio
     */
    private constructAddress(audioType: AudioX): string {
        // What socket type we gonna use ws / wss
        const secure = location.protocol === "https:";
        let address = `${secure ? 'wss' : 'ws'}://${this.getDeviceServiceEndpoint}/devices/${audioType}?`;

        // Append auth.
        address += `auth=${this.auth}&`;

        // Append deviceX properties.
        let properties: DeviceSpec = null;

        // Get the desired device spec
        if (this.twoWayAudio) {
            properties = this.twoWayAudio;
        } else if (audioType === AudioX.receive) {
            properties = this.audioReceive.deviceSpec;

        } else if (audioType === AudioX.transmit) {
            properties = this.audioTransmit.deviceSpec;
        } else {
            return "";
        }

        // construct our API call.
        Object.keys(properties).forEach((key: string, index: number) => {
            let data = properties[key];
            address += `${key}=${data ?? ''}${index !== (Object.keys(properties).length -1) ? '&' : ''}`;
        });

        return address;
    }
}
